import React, { useState, useEffect } from 'react';
import { Activity, CheckCircle, Loader2 } from 'lucide-react';

interface Stage {
  id: string;
  name: string;
  component: React.ReactNode;
  status: 'pending' | 'loading' | 'complete';
  progress: number;
}

interface StageLoaderProps {
  onComplete: () => void;
}

export const StageLoader: React.FC<StageLoaderProps> = ({ onComplete }) => {
  const [stages, setStages] = useState<Stage[]>([
    {
      id: 'raw-water',
      name: 'Raw Water Stage',
      component: null,
      status: 'pending',
      progress: 0
    },
    {
      id: 'pre-treatment',
      name: 'Pre-Treatment Stage',
      component: null,
      status: 'pending',
      progress: 0
    },
    {
      id: 'ultra-filtration',
      name: 'Ultra-Filtration Stage',
      component: null,
      status: 'pending',
      progress: 0
    },
    {
      id: 'de-chlorination',
      name: 'De-Chlorination Stage',
      component: null,
      status: 'pending',
      progress: 0
    },
    {
      id: 'reverse-osmosis',
      name: 'Reverse Osmosis Stage',
      component: null,
      status: 'pending',
      progress: 0
    },
    {
      id: 'ro-product',
      name: 'RO Product Stage',
      component: null,
      status: 'pending',
      progress: 0
    }
  ]);

  useEffect(() => {
    const loadStages = async () => {
      for (let i = 0; i < stages.length; i++) {
        // Start loading stage
        setStages(prev => prev.map((stage, index) => 
          index === i ? { ...stage, status: 'loading' } : stage
        ));

        // Simulate progress
        for (let progress = 0; progress <= 100; progress += 5) {
          await new Promise(resolve => setTimeout(resolve, 100));
          setStages(prev => prev.map((stage, index) =>
            index === i ? { ...stage, progress } : stage
          ));
        }

        // Complete stage
        setStages(prev => prev.map((stage, index) =>
          index === i ? { ...stage, status: 'complete', progress: 100 } : stage
        ));

        // Add delay between stages
        await new Promise(resolve => setTimeout(resolve, 2000));
      }

      // All stages complete
      setTimeout(() => {
        onComplete();
      }, 2000);
    };

    loadStages();
  }, []);

  return (
    <div className="max-w-4xl mx-auto mt-8 p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold text-gray-900 mb-8">Initializing System Components</h2>
      
      <div className="space-y-6">
        {stages.map((stage) => (
          <div key={stage.id} className="relative">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-3">
                {stage.status === 'complete' ? (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                ) : stage.status === 'loading' ? (
                  <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
                ) : (
                  <Activity className="h-5 w-5 text-gray-400" />
                )}
                <span className="text-sm font-medium text-gray-900">{stage.name}</span>
              </div>
              <span className="text-sm text-gray-500">{stage.progress}%</span>
            </div>
            
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-500 rounded-full ${
                  stage.status === 'complete'
                    ? 'bg-green-500'
                    : 'bg-blue-500'
                }`}
                style={{ width: `${stage.progress}%` }}
              />
            </div>

            {stage.status === 'loading' && (
              <div className="mt-2">
                <p className="text-sm text-gray-500 animate-pulse">
                  {stage.progress < 33 ? 'Initializing components...' :
                   stage.progress < 66 ? 'Configuring parameters...' :
                   'Finalizing setup...'}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-8 text-center text-sm text-gray-500">
        {stages.every(stage => stage.status === 'complete')
          ? 'All stages initialized successfully!'
          : 'Please wait while the system initializes...'}
      </div>
    </div>
  );
};